import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Type, RotateCcw, Save, Upload } from "lucide-react";
import { AZ, loadOverrides } from "../i18n";
import { storageMediaUrl } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-lg border border-white/15 bg-surface px-3 py-2 text-sm text-white outline-none transition-colors focus:border-gold";

const SPECIAL = [
  { key: "image:brand_logo", label: "YUXEL brand logo", hint: "Upload the official transparent PNG/SVG logo. Used in header and footer." },
  { key: "image:founder_photo", label: "Founder photo (About)", hint: "Upload from computer or paste a URL." },
  { key: "image:about_hero", label: "About hero image", hint: "Hero background on the About page." },
  { key: "image:services_hero", label: "Services hero image", hint: "Hero background on the Services index page." },
  { key: "video:home_hero", label: "Home hero video", hint: "Upload an optimized MP4/WebM (max 50 MB). If it cannot autoplay, the poster image is shown automatically." },
  { key: "image:home_hero_poster", label: "Home hero poster", hint: "Fallback image shown while the hero video loads or when motion is reduced." },
  { key: "image:home_neutral_hero", label: "Home hero fallback background", hint: "Legacy fallback image. The video poster above is preferred." },
  { key: "text:founder_name", label: "Founder name (About)" },
];

const ALL_KEYS = Object.keys(AZ).filter((k) => !/meridian|aurora|solmar|éclat|eclat|vela district|illustrative|placeholder/i.test(k));
const PRESETS = {
  Homepage: [
    "Business Transformation Company",
    "Marketing makes a business visible.",
    "Systems make it rise.",
    "YUXEL turns strategy, brand, marketing and sales into one commercial system for medium and large businesses — so growth is designed, not left to chance.",
    "Discuss your project", "View the work", "Selected experience", "Projects contributed to prior to YUXEL.",
    "Selected projects from professional work before YUXEL and current portfolio experience.",
    "What does YUXEL change?", "The problem is often not marketing.", "It is the system marketing is disconnected from.",
    "YUXEL connects positioning, the offer, demand, sales and measurement so the business is managed as one commercial mechanism.",
    "Where does your problem begin?", "Start with the business problem, not a service package.",
    "Seven levels of growth", "Seven connected levels. One system for growth.", "Capabilities", "Different disciplines. One commercial objective."
  ],
  About: [
    "About YUXEL", "We were not founded to be a marketing agency.",
    "YUXEL builds the system that determines how a business grows. Strategy, business architecture, brand, marketing, sales and analytics operate as one commercial mechanism.",
    "Our role", "One direction. The right experts. Clear accountability.",
    "YUXEL manages diagnosis, strategy, positioning, marketing and sales systems, creative direction and measurement. Selected production partners deliver video, photography, editing, motion and technical production to our brief and quality standards.",
    "Leadership", "How we work", "Four principles we don't negotiate."
  ],
  Contact: [
    "Contact", "Let’s discuss the next stage of your business.",
    "Share where your business is, where it wants to go and what stands in the way. If there is a fit, our first meeting will establish the direction for the next stage.",
    "Industry", "Select industry", "Main problem or need *", "What do you need built?", "Planned project budget", "Select range",
    "Your main ambition for the next 12 months *", "Discuss the project",
    "Your information is used only to assess collaboration fit and is not shared with third parties."
  ],
  "Navigation / Footer": [
    "Services", "Projects", "YUXEL Intelligence", "About YUXEL", "Contact", "Discuss your project",
    "Business Transformation Company. Strategy, brand, marketing and sales systems for medium and large businesses.",
    "All services →", "Company", "Sectors", "Real Estate Marketing", "Construction", "HoReCa Marketing", "Entertainment", "© 2026 YUXEL"
  ],
};


async function uploadMedia(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/admin/media`, { method: "POST", credentials: "include", body: fd });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || "Upload failed");
  return data.url;
}

const Row = ({ k, label, hint, value, onSaved }) => {
  const defaultEn = k.includes(":") ? "" : k;
  const [en, setEn] = useState(value?.en || defaultEn);
  const [az, setAz] = useState(value?.az || (k.includes(":") ? "" : (AZ[k] || "")));
  const [busy, setBusy] = useState(false);
  const long = k.length > 90;
  const Cmp = long ? "textarea" : "input";
  const isImage = k.startsWith("image:");
  const isVideo = k.startsWith("video:");
  const isMedia = isImage || isVideo;

  const put = async (payload) => {
    setBusy(true);
    try {
      const safePayload = isMedia
        ? { en: storageMediaUrl(payload.en || ""), az: storageMediaUrl(payload.az || "") }
        : payload;
      const r = await fetch(`${API}/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ key: k, value: safePayload }),
      });
      if (!r.ok) throw new Error("Save failed");
      await loadOverrides(true);
      onSaved(k, safePayload.en || safePayload.az ? safePayload : null);
      toast.success(safePayload.en || safePayload.az ? "Saved — refresh the site to see it." : "Reset to default.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const url = await uploadMedia(file);
      setEn(url);
      setAz(url);
      await put({ en: url, az: url });
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <li className="border-t border-white/10 py-4" data-testid="content-row">
      <p className="text-xs text-white/50">{label || "Original (EN)"}</p>
      <p className="mt-1 text-sm text-white">{label ? "" : k}</p>
      {hint && <p className="text-[11px] text-mist">{hint}</p>}

      {isMedia && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:border-gold hover:text-gold">
            <Upload className="h-4 w-4" /> {isVideo ? "Upload video" : "Upload from computer"}
            <input className="sr-only" type="file" accept={isVideo ? "video/mp4,video/webm" : "image/*"} onChange={(e) => { const file = e.target.files?.[0]; if (file) upload(file); e.target.value = ""; }} />
          </label>
          {(en || az) && <span className="max-w-md truncate text-[11px] text-white/40">{en || az}</span>}
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">EN</label>
          <Cmp rows={3} data-testid="content-en-input" value={en} onChange={(e) => setEn(e.target.value)} placeholder={label ? "" : k} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">AZ</label>
          <Cmp rows={3} data-testid="content-az-input" value={az} onChange={(e) => setAz(e.target.value)} placeholder={AZ[k] || ""} className={inputCls} />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button type="button" data-testid="content-save-button" disabled={busy} onClick={() => put({ en, az })} className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-1.5 text-xs font-semibold text-ink hover:bg-champagne disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save</button>
        {isImage && k === "image:founder_photo" && <button type="button" data-testid="content-remove-founder-photo" disabled={busy} onClick={() => { setEn("__NONE__"); setAz("__NONE__"); put({ en: "__NONE__", az: "__NONE__" }); }} className="inline-flex items-center gap-1.5 rounded-full border border-red-300/30 px-4 py-1.5 text-xs font-semibold text-red-200 hover:border-red-300">Remove image</button>}
        {value && <button type="button" data-testid="content-reset-button" disabled={busy} onClick={() => { setEn(""); setAz(""); put({ en: "", az: "" }); }} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white hover:border-gold hover:text-gold"><RotateCcw className="h-3.5 w-3.5" /> Reset to default</button>}
      </div>
    </li>
  );
};

export const ContentEditor = () => {
  const [overrides, setOverrides] = useState({});
  const [q, setQ] = useState("");
  const [custom, setCustom] = useState("");
  const [group, setGroup] = useState("Homepage");

  useEffect(() => { loadOverrides(true).then(setOverrides); }, []);

  const onSaved = (k, v) => setOverrides((o) => { const n = { ...o }; if (v) n[k] = v; else delete n[k]; return n; });

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle) return ALL_KEYS.filter((k) => k.toLowerCase().includes(needle) || (AZ[k] || "").toLowerCase().includes(needle)).slice(0, 80);
    if (group === "All site text") return ALL_KEYS;
    return (PRESETS[group] || []).filter(Boolean);
  }, [q, group]);

  return (
    <section data-testid="content-editor" className="mt-10 border border-white/10 bg-panel p-6 sm:p-8">
      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold"><Type className="h-4 w-4" /> Site content (EN / AZ)</p>
      <p className="mt-3 text-sm text-mist">Ana səhifə, Haqqımızda, Əlaqə, menyu/footer və saytın bütün digər mətnlərini AZ/EN dəyişin. Xidmətlər üçün ayrıca idarəetmə bölməsi var.</p>

      <ul className="mt-6" data-testid="content-special-list">
        {SPECIAL.map((s) => <Row key={s.key + JSON.stringify(overrides[s.key] || null)} k={s.key} label={s.label} hint={s.hint} value={overrides[s.key]} onSaved={onSaved} />)}
      </ul>

      <div className="mt-8 flex flex-wrap gap-2">
        {[...Object.keys(PRESETS), "All site text"].map((name) => (
          <button key={name} type="button" onClick={() => { setGroup(name); setQ(""); }} className={`rounded-full border px-4 py-2 text-xs font-semibold ${group === name && !q ? "border-gold bg-gold text-ink" : "border-white/15 text-white/70 hover:border-gold hover:text-gold"}`}>{name}</button>
        ))}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <input data-testid="content-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text… e.g. Book a consultation, Xidmətlər" className={inputCls} />
        <div className="flex gap-2"><input data-testid="content-custom-key-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Or paste exact English text not found by search" className={inputCls} /><button type="button" data-testid="content-custom-key-button" onClick={() => { if (custom.trim()) setQ(custom.trim()); }} className="shrink-0 rounded-full border border-white/20 px-4 text-xs font-semibold text-white hover:border-gold hover:text-gold">Edit</button></div>
      </div>

      <ul className="mt-4" data-testid="content-results">
        {!q && results.length === 0 && <li className="py-4 text-sm text-mist" data-testid="content-empty">No editable text found in this section.</li>}
        {q && results.length === 0 && <Row key={`custom-${q}`} k={q.trim()} value={overrides[q.trim()]} onSaved={onSaved} />}
        {results.map((k) => <Row key={k + JSON.stringify(overrides[k] || null)} k={k} value={overrides[k]} onSaved={onSaved} />)}
      </ul>
    </section>
  );
};
